import React, { useCallback, useMemo, useState } from "react";
import {
  Users,
  Calendar,
  Clock,
  MapPin,
  MessageCircle,
  UserRoundCheck,
  Timer,
  ClipboardList,
  MoreHorizontal,
  Hand,
  Ban,
  Loader2,
  UserPlus,
  ChevronDown
} from "lucide-react";
import { format } from "date-fns";

import { getInitials } from "../../../../../utils/getInitials";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../../../../../components/eds/EDSDropdownMenu";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Enquiry, EnquiryStatus } from "../../../../../types/enquiries";
import { User } from "../../../../../types/VenueGroup";
import { EDSBadge } from "../../../../../components/eds/EDSBadge";
import { useDiaryContext } from "../../../../../context/DiaryContext";
import { useUserContext } from "../../../../../hooks/useUserContext";
import { getBookingTypeById } from "../../../../../utils/singleBooking";
import { STATUS_CONFIG, STATUS_ACTIONS } from "../constants";
import { CopyableBookingRef } from "../../../../../components/Shared/CopyableBookingRef/CopyableBookingRef";
import { AssignToModal } from "./AssignToModal";

export interface EnquiryCardProps {
  enquiry: Enquiry;
  users: User[];
  isRecentlyUpdated: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  onTake: (id: string) => void;
  onStatusChange: (id: string, status: EnquiryStatus) => void;
  onAssign: (id: string, userId: string | undefined) => void;
  onView: (enquiry: Enquiry) => void;
}

const EnquiryCardComponent: React.FC<EnquiryCardProps> = ({
  enquiry,
  users,
  isRecentlyUpdated,
  isSelected,
  onToggleSelect,
  onTake,
  onStatusChange,
  onAssign,
  onView
}) => {
  const { venueConfig } = useDiaryContext();
  const { user: currentUser } = useUserContext();

  const bookingTypeColour = useMemo(() => {
    if (!enquiry.booking_type) return undefined;
    const venueBookingType = getBookingTypeById({
      bookingTypeId: enquiry.booking_type.id,
      venueConfig
    });
    return venueBookingType?.color ?? enquiry.booking_type.color;
  }, [enquiry.booking_type?.id, enquiry.booking_type?.color, venueConfig]);

  const assignedUser = useMemo(() => {
    if (!enquiry.assigned_to) return undefined;
    return users.find((user) => user._id === enquiry.assigned_to);
  }, [enquiry.assigned_to, users]);

  const statusInfo = STATUS_CONFIG[enquiry.status];
  const eventDate = enquiry.date ? new Date(enquiry.date) : null;
  const createdDate = enquiry.created ? new Date(enquiry.created) : null;

  const waitingHours = createdDate
    ? (Date.now() - createdDate.getTime()) / (1000 * 60 * 60)
    : null;
  const isWaitingTwoHours =
    waitingHours !== null && waitingHours >= 2 && enquiry.status === "new";

  const hasMention =
    enquiry.comments?.some((comment) => comment.text?.includes("@")) ?? false;

  const [isStatusHovering, setIsStatusHovering] = useState(false);

  const getDefaultStatusIcon = (): React.ReactNode => {
    if (enquiry.status === "in_progress") return <Loader2 size={14} />;
    if (enquiry.status === "cancelled") return <Ban size={14} />;
    return statusInfo.initial;
  };

  const defaultStatusIcon = getDefaultStatusIcon();

  const statusIconContent = isStatusHovering ? (
    <ChevronDown size={14} />
  ) : (
    defaultStatusIcon
  );

  const filteredStatusActions = useMemo(
    () => STATUS_ACTIONS.filter((action) => action.value !== enquiry.status),
    [enquiry.status]
  );

  const customerName =
    [enquiry.customer?.first_name, enquiry.customer?.last_name]
      .filter(Boolean)
      .join(" ")
      .trim() || "Guest";

  const handleCardClick = useCallback(() => {
    onView(enquiry);
  }, [onView, enquiry]);

  const handleTakeClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onTake(enquiry._id);
    },
    [onTake, enquiry._id]
  );

  const handleStatusChange = useCallback(
    (status: EnquiryStatus) => {
      onStatusChange(enquiry._id, status);
    },
    [onStatusChange, enquiry._id]
  );

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const handleAssignToClick = useCallback(() => {
    setIsAssignModalOpen(true);
  }, []);

  const handleAssignSave = useCallback(
    (userId: string | undefined) => {
      onAssign(enquiry._id, userId);
      setIsAssignModalOpen(false);
    },
    [onAssign, enquiry._id]
  );

  const handleAssignCancel = useCallback(() => {
    setIsAssignModalOpen(false);
  }, []);

  const handleCheckboxChange = useCallback(() => {
    onToggleSelect?.(enquiry._id);
  }, [onToggleSelect, enquiry._id]);

  const showCheckbox = onToggleSelect !== undefined;

  return (
    <div
      className={`relative ${isRecentlyUpdated ? "animate-pulse bg-green-500/10" : ""}`}
    >
      {showCheckbox && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 flex items-center">
          <Checkbox
            checked={isSelected ?? false}
            onCheckedChange={handleCheckboxChange}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
              }
            }}
            aria-label={`Select enquiry from ${customerName}`}
          />
        </div>
      )}

      {/* Status icon - positioned consistently for all statuses */}
      {enquiry.status === "new" ? (
        <button
          className={`absolute ${showCheckbox ? "left-10" : "left-3"} top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full flex items-center justify-center ${statusInfo.bgClass} ${statusInfo.textClass} hover:opacity-80 transition-opacity`}
          onClick={handleTakeClick}
          aria-label="Take enquiry"
          title="Take enquiry"
        >
          <Hand size={16} />
        </button>
      ) : (
        <div
          className={`absolute ${showCheckbox ? "left-10" : "left-3"} top-1/2 -translate-y-1/2 z-10`}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${statusInfo.bgClass} ${statusInfo.textClass} hover:opacity-80 transition-opacity cursor-pointer relative`}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => setIsStatusHovering(true)}
                onMouseLeave={() => setIsStatusHovering(false)}
                aria-label={`Change status (currently ${statusInfo.label})`}
                title={`Change status (currently ${statusInfo.label})`}
              >
                {statusIconContent}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {filteredStatusActions.map((action) => (
                <DropdownMenuItem
                  key={action.value}
                  onSelect={() => handleStatusChange(action.value)}
                >
                  {action.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <div className="absolute right-3 top-3 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 rounded-md hover:bg-accent transition-colors"
              onClick={(e) => e.stopPropagation()}
              aria-label="More options"
            >
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={handleAssignToClick}>
              <UserPlus size={14} className="mr-2" />
              Assign to
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Main clickable button covering the content area */}
      <button
        className={`w-full flex items-center gap-3 p-3 ${showCheckbox ? "pl-[4.5rem]" : "pl-14"} pr-16 border-b border-border bg-card hover:bg-accent/50 cursor-pointer transition-colors text-left border-0 outline-none`}
        onClick={handleCardClick}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-foreground truncate">
              {customerName}
            </span>

            <div className="flex items-center gap-1">
              {(enquiry.unread_messages ?? 0) > 0 && (
                <MessageCircle
                  className="text-blue-500"
                  size={14}
                  aria-label={`${enquiry.unread_messages} unread messages`}
                />
              )}
              {enquiry.follow_up && (
                <UserRoundCheck
                  className="text-amber-500"
                  size={14}
                  aria-label="Follow up required"
                />
              )}
              {isWaitingTwoHours && (
                <Timer
                  className="text-red-500"
                  size={14}
                  aria-label="Waiting 2+ hours"
                />
              )}
              {hasMention && (
                <ClipboardList
                  className="text-purple-500"
                  size={14}
                  aria-label="Mentioned in notes"
                />
              )}
            </div>

            {enquiry.booking_type && (
              <EDSBadge
                className="DiarySidebarListItem__detailsType ml-auto"
                variant="default"
                style={
                  {
                    "--booking-type-color":
                      bookingTypeColour ?? "hsl(var(--primary))",
                    "--booking-type-foreground": bookingTypeColour
                      ? "white"
                      : "hsl(var(--primary-foreground))",
                    maxWidth: "140px"
                  } as React.CSSProperties
                }
              >
                <span
                  className="DiarySidebarListItem_detailsTypeText truncate max-w-[120px] inline-block"
                  title={enquiry.booking_type.name}
                >
                  {enquiry.booking_type.name}
                </span>
              </EDSBadge>
            )}
          </div>

          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
            {assignedUser ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2 py-1 text-xs font-medium"
                aria-label={`Assigned to ${assignedUser.full_name}`}
              >
                <span
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none"
                  aria-hidden="true"
                >
                  {assignedUser.initials || getInitials(assignedUser.full_name)}
                </span>
                <span
                  className="truncate max-w-[120px]"
                  title={assignedUser.full_name}
                >
                  {assignedUser.full_name}
                </span>
              </span>
            ) : (
              <span
                className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2 py-1 text-xs font-medium text-muted-foreground"
                aria-label="Unassigned"
              >
                Unassigned
              </span>
            )}

            {enquiry.num_people && (
              <span className="flex items-center gap-1">
                <Users size={14} />
                {enquiry.num_people}
              </span>
            )}

            {eventDate && (
              <>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {format(eventDate, "EEE d MMM yyyy")}
                </span>
                {enquiry.time && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {enquiry.time}
                  </span>
                )}
              </>
            )}

            {enquiry.venue?.title && (
              <span className="flex items-center gap-1">
                <MapPin size={14} />
                {enquiry.venue.title}
              </span>
            )}

            <span className="flex items-center gap-1 text-xs text-muted-foreground/70 ml-auto">
              <CopyableBookingRef
                reference={enquiry.reference}
                className="text-xs text-muted-foreground/70 font-normal"
              />
            </span>
          </div>
        </div>
      </button>

      <AssignToModal
        open={isAssignModalOpen}
        users={users}
        currentAssignedTo={enquiry.assigned_to}
        currentUserId={currentUser?._id}
        onSave={handleAssignSave}
        onCancel={handleAssignCancel}
      />
    </div>
  );
};

export const EnquiryCard = React.memo(EnquiryCardComponent);
EnquiryCard.displayName = "EnquiryCard";
